/**
 * Drag a garment to the rail.
 *
 * Immersive desktop only. Dragging the product image toward the left edge wakes
 * the rail; releasing over it hangs the piece. Anywhere else, the image springs
 * back — nothing is lost by trying.
 *
 * GSAP (Draggable + Flip) is fetched on first drag rather than on page load:
 * it is ~110kB and standard mode never touches it.
 */

/* Inlined rather than imported — see the note at the top of mode.js. */
const getMode = () =>
  document.documentElement.classList.contains('mode-immersive') ? 'immersive' : 'standard';
const canBeImmersive = () =>
  window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let gsapPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const el = document.createElement('script');
    el.src = src;
    el.onload = resolve;
    el.onerror = () => reject(new Error(`could not load ${src}`));
    document.head.appendChild(el);
  });
}

/* Core first — the plugins register themselves against it. */
function loadGsap() {
  if (gsapPromise) return gsapPromise;
  const assets = window.SS_ASSETS || {};
  gsapPromise = loadScript(assets.gsap)
    .then(() => Promise.all([loadScript(assets.draggable), loadScript(assets.flip)]))
    .then(() => {
      window.gsap.registerPlugin(window.Draggable, window.Flip);
      return window.gsap;
    })
    .catch((error) => {
      gsapPromise = null;
      throw error;
    });
  return gsapPromise;
}

function initDragHang(scope = document) {
  const garment = scope.querySelector('[data-viewer-garment]');
  const hangBtn = document.querySelector('[data-hang]');
  const rail = document.querySelector('[data-rail]');
  if (!garment || !hangBtn || !rail || garment.dataset.dragReady) return;
  garment.dataset.dragReady = 'true';

  const railEdge = rail.querySelector('.rail__edge');
  let draggable = null;
  let armed = false;

  /* Build the item from the same attributes the hang button uses, so a drag
     and a click put identical data on the rail. */
  function itemFromForm() {
    const form = document.getElementById('product-form');
    const variantId = Number(form?.querySelector('[data-variant-id]')?.value);
    if (!variantId) return null;

    let variantTitle = '';
    try {
      const variants = JSON.parse(document.querySelector('[data-variant-data]').textContent);
      const match = variants.find((v) => v.id === variantId);
      if (match && match.title !== 'Default Title') variantTitle = match.title;
    } catch (e) {
      /* fall through with an empty variant title */
    }

    return {
      variantId,
      handle: hangBtn.dataset.productHandle,
      title: hangBtn.dataset.productTitle,
      variantTitle,
      image: hangBtn.dataset.productImage || ''
    };
  }

  const overRail = (x) => x <= railEdge.getBoundingClientRect().right + 40;

  async function enable() {
    if (draggable) return;
    let gsap;
    try {
      gsap = await loadGsap();
    } catch (e) {
      return; // no drag, but the hang button still works
    }
    // The mode may have changed while GSAP was in flight.
    if (getMode() !== 'immersive' || !canBeImmersive()) return;

    garment.classList.add('product__drag-proxy');

    [draggable] = window.Draggable.create(garment, {
      type: 'x,y',
      inertia: false,
      cursor: 'grab',
      activeCursor: 'grabbing',
      onDragStart() {
        garment.classList.add('is-dragging');
      },
      onDrag() {
        const near = overRail(this.pointerX);
        if (near !== armed) {
          armed = near;
          rail.classList.toggle('is-armed', near);
          if (near) rail.classList.add('is-open');
        }
      },
      onDragEnd() {
        garment.classList.remove('is-dragging');
        const dropped = overRail(this.pointerX);
        rail.classList.remove('is-armed');
        armed = false;

        if (dropped) {
          const item = itemFromForm();
          if (item) {
            document.dispatchEvent(
              new CustomEvent('ss:hang', { detail: { item, origin: garment } })
            );
          }
        }

        // Always return the image to where it belongs.
        gsap.to(garment, { x: 0, y: 0, duration: 0.5, ease: 'power3.out' });
      }
    });
  }

  function disable() {
    if (!draggable) return;
    draggable.kill();
    draggable = null;
    garment.classList.remove('product__drag-proxy', 'is-dragging');
    garment.style.transform = '';
    rail.classList.remove('is-armed');
  }

  /* GSAP is fetched on hover intent, not on page load: genuinely lazy, but
     ready by the time a press follows. Nothing is downloaded for a visitor who
     never reaches for the garment, and nothing at all in standard mode. */
  let armedForLoad = false;

  function armLoading() {
    if (armedForLoad) return;
    armedForLoad = true;
    garment.addEventListener('pointerenter', onIntent, { once: true });
    garment.addEventListener('focusin', onIntent, { once: true });
  }

  function onIntent() {
    if (getMode() === 'immersive' && canBeImmersive()) enable();
  }

  function disarmLoading() {
    armedForLoad = false;
    garment.removeEventListener('pointerenter', onIntent);
    garment.removeEventListener('focusin', onIntent);
  }

  const sync = () => {
    if (getMode() === 'immersive' && canBeImmersive()) armLoading();
    else {
      disarmLoading();
      disable();
    }
  };

  sync();
  document.addEventListener('ss:modechange', sync);
}

initDragHang();
document.addEventListener('shopify:section:load', (event) => initDragHang(event.target));
